// Quick entry form for blood pressure events
import React, { useState } from 'react';
import { LocationInput } from './LocationInput';
import { LocationData } from '../types';
import { healthAPI } from '../api';
import './QuickEntry.css';

interface BloodPressureFormProps {
    onSuccess?: (traceId: string) => void;
    onError?: (error: string) => void;
}

export const BloodPressureForm: React.FC<BloodPressureFormProps> = ({ onSuccess, onError }) => {
    const [systolic, setSystolic] = useState<string>('');
    const [diastolic, setDiastolic] = useState<string>('');
    const [pulse, setPulse] = useState<string>('');
    const [context, setContext] = useState<string>('');
    const [location, setLocation] = useState<LocationData | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!systolic || !diastolic) {
            if (onError) onError('Please enter both systolic and diastolic values');
            return;
        }

        setIsSubmitting(true);

        try {
            const data: any = {
                systolic: parseInt(systolic),
                diastolic: parseInt(diastolic),
                pulse: pulse ? parseInt(pulse) : undefined,
                notes: context
            };

            if (location) {
                data.location = location;
            }

            const requestBody: any = {
                type: 'blood_pressure',
                data,
            };

            const response = await healthAPI.createEvent(requestBody);

            // Reset form
            setSystolic('');
            setDiastolic('');
            setPulse('');
            setContext('');

            if (onSuccess) {
                onSuccess(response.trace_id);
            }
        } catch (error) {
            console.error('Error logging blood pressure:', error);
            if (onError) {
                onError(error instanceof Error ? error.message : 'Unknown error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="quick-entry-form bp-form">
            <h3>❤️ Blood Pressure Entry</h3>

            <div className="form-group" style={{ display: 'flex', gap: '15px' }}>
                <div style={{ flex: 1 }}>
                    <label>Systolic (Top)</label>
                    <input
                        type="number"
                        value={systolic}
                        onChange={(e) => setSystolic(e.target.value)}
                        placeholder="120"
                        className="food-input"
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label>Diastolic (Bottom)</label>
                    <input
                        type="number"
                        value={diastolic}
                        onChange={(e) => setDiastolic(e.target.value)}
                        placeholder="80"
                        className="food-input"
                    />
                </div>
            </div>

            <div className="form-group">
                <label>Pulse (BPM) (optional)</label>
                <input
                    type="number"
                    value={pulse}
                    onChange={(e) => setPulse(e.target.value)}
                    placeholder="e.g. 72"
                    className="food-input"
                    style={{ width: '50%' }}
                />
            </div>

            <div className="form-group">
                <label>Context (optional)</label>
                <input
                    type="text"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="e.g. 'At doctor office', 'Just woke up'"
                    className="food-input"
                />
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                    Mention time (e.g. "9am") or location if you're elsewhere.
                </div>
            </div>

            <LocationInput onLocationChange={setLocation} />

            <button type="submit" className="submit-button" disabled={isSubmitting || !systolic || !diastolic}>
                {isSubmitting ? 'Logging...' : 'Log Blood Pressure'}
            </button>
        </form>
    );
};
